import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, ArrowRight, Layers, ShieldAlert, Plus, Trash2 } from "lucide-react";

export default function Tradelines() {
  const [providers, setProviders] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", url: "", description: "" });

  const load = () => {
    api.get("/tradeline-providers").then((r) => setProviders(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name.trim()) return toast.error("Company name is required");
    setSaving(true);
    try {
      await api.post("/tradeline-providers", form);
      toast.success("Tradeline company added to directory");
      setOpen(false);
      setForm({ name: "", url: "", description: "" });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const remove = async (id) => { await api.delete(`/tradeline-providers/${id}`); load(); };
  const hrefOf = (u) => (u.startsWith("http") ? u : `https://${u}`);

  return (
    <div className="animate-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase text-primary font-bold mb-2">Boost Credit Profiles</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-none">Tradelines</h1>
          <p className="text-muted-foreground mt-3">Authorized-user tradeline providers and business credit lines you can attach to a client or entity.</p>
        </div>
        <Button className="rounded-lg" onClick={() => setOpen(true)} data-testid="add-company-btn"><Plus className="h-4 w-4 mr-1" /> Add Company</Button>
      </div>

      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-200/90 flex gap-3 mb-8" data-testid="tradelines-disclaimer">
        <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5" />
        <p>Adding a client as an authorized user on a seasoned account is legal and can boost their profile. All data reported to the bureaus must be accurate — we do not fabricate or falsify account history.</p>
      </div>

      <h3 className="font-display text-xl font-semibold mb-3">Provider Directory</h3>
      <div className="grid sm:grid-cols-2 gap-4 mb-10">
        {providers.map((p) => (
          <Card key={p.id || p.name} className="p-5 bg-card/60 border-border hover:-translate-y-1 transition-transform" data-testid={`directory-${p.id || p.name}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 font-display font-semibold text-lg"><Building2 className="h-5 w-5 text-primary" /> {p.name}</div>
              {p.custom && (
                <button onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive" data-testid={`directory-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
              )}
            </div>
            {p.description && <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{p.description}</p>}
            <div className="flex items-center justify-between mt-4">
              <span className="text-[10px] uppercase tracking-widest text-primary font-semibold">{p.specialty || (p.custom ? "Custom provider" : "")}</span>
              {p.url
                ? <a href={hrefOf(p.url)} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1" data-testid={`directory-link-${p.id || p.name}`}>Visit site <ArrowRight className="h-3.5 w-3.5" /></a>
                : <span className="text-xs text-muted-foreground">No website</span>}
            </div>
          </Card>
        ))}
      </div>

      <h3 className="font-display text-xl font-semibold mb-3">Attach to a Client</h3>
      <p className="text-sm text-muted-foreground mb-3">Open a client and use the Tradelines tab to link and track purchased tradelines against their profile.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {clients.map((c) => (
          <Link key={c.id} to={`/clients/${c.id}`} className="flex items-center justify-between p-4 rounded-lg border border-border bg-card/60 hover:border-primary/40 transition-colors" data-testid={`tl-client-${c.id}`}>
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold text-sm">{c.name?.[0]?.toUpperCase()}</div>
              <span className="font-medium text-sm">{c.name}</span>
            </div>
            <Layers className="h-4 w-4 text-muted-foreground" />
          </Link>
        ))}
        {!clients.length && <p className="text-sm text-muted-foreground py-4">No clients yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card/90 backdrop-blur-xl border-border" description="Add your own tradeline company to the shared directory">
          <DialogHeader><DialogTitle className="font-display">Add Tradeline Company</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Company Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-name" /></div>
            <div className="space-y-2"><Label>Website URL</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://theircompany.com" data-testid="company-url" /></div>
            <div className="space-y-2"><Label>Short Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. My go-to seasoned AU tradelines" data-testid="company-description" /></div>
          </div>
          <DialogFooter><Button onClick={add} disabled={saving} className="rounded-lg" data-testid="save-company-btn">{saving ? "Saving…" : "Add Company"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
